import { apiClient } from '@/lib/api-client'
import { config } from '@/config'
import i18n from '@/lib/i18n'
import type {
  PreviewChunk,
  PreviewProgress,
  PreviewResult,
  PreviewSummary,
} from '@/types/preview'

/**
 * Upload a PDF and run the whole pipeline, streaming progress.
 *
 * Uses fetch rather than axios because this endpoint returns SSE and we want
 * to render each stage as it lands instead of waiting for the whole run.
 */
/** 32 hex chars — the id shape the backend accepts from a client. */
export function newSessionId(): string {
  return crypto.randomUUID().replace(/-/g, '')
}

export async function runPreview(
  file: File,
  opts: { title?: string; domains?: string; languages?: string; sessionId?: string },
  onProgress: (p: PreviewProgress) => void,
  signal?: AbortSignal,
): Promise<PreviewResult> {
  const form = new FormData()
  form.append('file', file)
  form.append('title', opts.title ?? '')
  form.append('domains', opts.domains ?? '')
  form.append('languages', opts.languages ?? '')
  // Supplied by the caller so the URL can carry the id before the upload starts.
  if (opts.sessionId) form.append('session_id', opts.sessionId)

  const res = await fetch(`${config.BACKEND_URL}/previews`, {
    method: 'POST',
    body: form,
    signal,
    // fetch drops cookies unless asked; without this the upload is a 401.
    credentials: 'include',
  })
  if (res.status === 401) throw new Error(i18n.t('errors.auth_required'))
  if (!res.ok || !res.body) {
    throw new Error(i18n.t('errors.uploadFailed', { status: res.status }))
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let result: PreviewResult | null = null

  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })

    // SSE frames are separated by a blank line; keep the trailing partial.
    const frames = buffer.split('\n\n')
    buffer = frames.pop() ?? ''

    for (const frame of frames) {
      const line = frame.split('\n').find((l) => l.startsWith('data: '))
      if (!line) continue
      let payload: PreviewProgress & Partial<PreviewResult>
      try {
        payload = JSON.parse(line.slice(6))
      } catch {
        continue
      }
      if (payload.stage === 'error') throw new Error(payload.message)
      onProgress(payload)
      if (payload.stage === 'complete') result = payload as unknown as PreviewResult
    }
  }

  if (!result) throw new Error(i18n.t('errors.pipelineNoResult'))
  return result
}

/** Re-open an uncommitted preview (terms + mentions) without re-running it. */
export async function getPreview(sessionId: string): Promise<PreviewResult> {
  const { data } = await apiClient.get<PreviewResult>(`/previews/${sessionId}`)
  return { ...data, chunks: data.chunks ?? 0 }
}

export interface PreviewPrompt {
  prompt: string
  default_prompt: string
  is_custom: boolean
}

/** The prompt this preview ran with, plus the default to reset back to. */
export async function getPreviewPrompt(
  sessionId: string,
  domains: string,
): Promise<PreviewPrompt> {
  const { data } = await apiClient.get<PreviewPrompt>(`/previews/${sessionId}/prompt`, {
    params: { domains },
  })
  return data
}

/**
 * Re-extract with a different prompt. Only the LLM step re-runs — the document
 * is already parsed and indexed — so this is a seconds-long loop, not a re-upload.
 */
export async function rerunPreview(
  sessionId: string,
  prompt: string,
  domains: string,
): Promise<PreviewResult> {
  const { data } = await apiClient.post<PreviewResult>(
    `/previews/${sessionId}/rerun`,
    { prompt, domains },
    { timeout: 600_000 },
  )
  return { ...data, chunks: data.chunks ?? 0 }
}

/** Parsed document text. The PDF itself is never stored — this is the only copy. */
export async function getPreviewText(sessionId: string): Promise<PreviewChunk[]> {
  const { data } = await apiClient.get<{ chunks?: PreviewChunk[] }>(
    `/previews/${sessionId}/text`,
  )
  return data.chunks ?? []
}

export interface PreviewSearchHit {
  text: string
  score: number
  page_number?: number
  title?: string
}

/** Search inside one preview only — the main corpus is untouched. */
export async function searchInPreview(
  sessionId: string,
  query: string,
  limit = 10,
): Promise<PreviewSearchHit[]> {
  const { data } = await apiClient.post<{ results: PreviewSearchHit[] }>(
    `/previews/${sessionId}/search`,
    { query, filters: { limit } },
  )
  return data.results ?? []
}

export async function listPreviews(): Promise<PreviewSummary[]> {
  const { data } = await apiClient.get<{ previews: PreviewSummary[] }>('/previews')
  return data.previews ?? []
}

/** Commit into a specific collection; omit to use the default one. */
export async function commitPreview(sessionId: string, collectionId?: number | null) {
  const { data } = await apiClient.post(`/previews/${sessionId}/commit`, {
    collection_id: collectionId ?? null,
  })
  return data
}

export async function discardPreview(sessionId: string) {
  const { data } = await apiClient.delete(`/previews/${sessionId}`)
  return data
}
